<template>
  <section id="about" class="section section--alt">
    <div class="about__inner">

      <!-- Content -->
      <div class="about__content">
        <div class="section-header">
          <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">About Me</p>
          <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">
            Data systems<br />engineered to scale
          </h2>
          <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
            I build production-grade data pipelines, cloud integrations, and
            warehouse architectures that keep data flowing reliably at scale.
            From ingestion on Azure Data Lake and AWS S3 to analytics delivery
            in Power BI and Synapse — I own the full data lifecycle.
          </p>
        </div>

        <!-- ETL Pipeline Visualization -->
        <div class="etl reveal reveal--d3" :class="{ 'is-visible': isVisible }">
          <div class="etl__label">ETL Pipeline Architecture</div>
          <div class="etl__flow">
            <div class="etl__stage etl__stage--extract">
              <div class="etl__stage-icon">📥</div>
              <div class="etl__stage-name">Extract</div>
              <div class="etl__stage-items">
                <span>APIs</span><span>Databases</span><span>Files</span><span>Streams</span>
              </div>
            </div>
            <div class="etl__arrow">
              <div class="etl__arrow-line">
                <div class="etl__arrow-particle"></div>
              </div>
              <svg width="10" height="14" viewBox="0 0 10 14"><path d="M0 0L10 7L0 14" fill="var(--color-accent)" opacity="0.6"/></svg>
            </div>
            <div class="etl__stage etl__stage--transform">
              <div class="etl__stage-icon">⚙️</div>
              <div class="etl__stage-name">Transform</div>
              <div class="etl__stage-items">
                <span>Clean</span><span>Enrich</span><span>Aggregate</span>
              </div>
            </div>
            <div class="etl__arrow">
              <div class="etl__arrow-line">
                <div class="etl__arrow-particle"></div>
              </div>
              <svg width="10" height="14" viewBox="0 0 10 14"><path d="M0 0L10 7L0 14" fill="var(--color-accent)" opacity="0.6"/></svg>
            </div>
            <div class="etl__stage etl__stage--load">
              <div class="etl__stage-icon">📊</div>
              <div class="etl__stage-name">Load</div>
              <div class="etl__stage-items">
                <span>Warehouse</span><span>Data Lake</span><span>Analytics</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tech stack tags -->
        <div class="about__stack reveal reveal--d4" :class="{ 'is-visible': isVisible }">
          <p class="about__stack-label">Core Stack</p>
          <div class="about__tags">
            <span class="tag" v-for="tech in coreStack" :key="tech">{{ tech }}</span>
          </div>
        </div>

        <!-- Quick info grid -->
        <div class="about__info reveal reveal--d5" :class="{ 'is-visible': isVisible }">
          <div class="about__info-item" v-for="item in infoItems" :key="item.label">
            <span class="about__info-label">{{ item.label }}</span>
            <span class="about__info-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()

const coreStack = [
  'Python', 'SQL', 'Apache Spark', 'Kafka', 'Apache Airflow',
  'Azure ADF', 'AWS Glue', 'Databricks', 'Synapse Analytics',
  'Docker', 'Kubernetes', 'Power BI',
]

const infoItems = [
  { label: 'Location', value: 'India 🇮🇳' },
  { label: 'Experience', value: '4+ Years' },
  { label: 'Role', value: 'Data Engineer' },
  { label: 'Focus', value: 'ETL · Cloud · Big Data' },
]
</script>

<style scoped>
.about__inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0;
  display: block;
}

.about__content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 24px 48px;
  align-items: start;
}

.about__content .section-header {
  grid-column: 1 / -1;
}

/* ETL visualization */
.etl {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  padding: 14px;
  margin-bottom: 16px;
}
.etl__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  margin-bottom: 16px;
}
.etl__flow {
  display: flex;
  align-items: center;
  gap: 0;
  overflow-x: auto;
}
.etl__stage {
  flex: 1;
  min-width: 100px;
  padding: 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-bg);
  text-align: center;
}
.etl__stage--extract { border-color: rgba(0, 212, 255, 0.3); }
.etl__stage--transform { border-color: rgba(139, 92, 246, 0.3); }
.etl__stage--load { border-color: rgba(52, 211, 153, 0.3); }

.etl__stage-icon { font-size: 20px; margin-bottom: 4px; }
.etl__stage-name { font-size: 12px; font-weight: 700; color: var(--color-text); margin-bottom: 8px; }
.etl__stage-items { display: flex; flex-direction: column; gap: 2px; }
.etl__stage-items span { font-size: 10px; color: var(--color-text-muted); font-family: var(--font-mono); }

.etl__arrow {
  display: flex;
  align-items: center;
  padding: 0 8px;
  flex-shrink: 0;
}
.etl__arrow-line {
  width: 32px; height: 2px;
  background: linear-gradient(to right, rgba(0,212,255,0.3), rgba(0,212,255,0.6));
  border-radius: 1px;
  position: relative;
  overflow: visible;
}
.etl__arrow-particle {
  position: absolute;
  top: 50%; transform: translateY(-50%);
  width: 6px; height: 6px;
  background: var(--color-accent);
  border-radius: 50%;
  animation: flow 2s linear infinite;
  box-shadow: 0 0 6px var(--color-accent);
}
@keyframes flow {
  0% { left: -6px; opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { left: 32px; opacity: 0; }
}

/* Stack tags */
.about__stack { margin-bottom: 14px; }
.about__stack-label {
  font-size: 11px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  margin-bottom: 12px;
}
.about__tags { display: flex; flex-wrap: wrap; gap: 8px; }

/* Info grid */
.about__info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.about__info-item {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.about__info-label {
  font-size: 10px; font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: var(--font-mono);
}
.about__info-value {
  font-size: 13px; font-weight: 500;
  color: var(--color-text);
}

@media (max-width: 1024px) {
  .about__content { grid-template-columns: 1fr; }
  .about__content .section-header { grid-column: 1; }
  .etl { padding: 12px; margin-bottom: 12px; }
  .etl__flow { gap: 4px; }
  .etl__stage { min-width: 80px; padding: 8px 6px; }
  .etl__stage-icon { font-size: 16px; }
  .etl__stage-name { font-size: 11px; }
  .etl__arrow-line { width: 16px; }
}
@media (max-width: 768px) {
  .about__content { grid-template-columns: 1fr; gap: 16px; }
  .etl__stage-items span { display: none; }
  .about__info { grid-template-columns: 1fr 1fr; }
  .about__tags { gap: 6px; }
}
@media (max-width: 480px) {
  .about__info { grid-template-columns: 1fr; }
}
</style>
